/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import { TextColumn, ACTION_VIEW } from '@veeam-vspc/components';
import { capitalize, NotificationDialogsTextKeys } from '@veeam-vspc/core';

import type { WizardStepData, CellRendererProps } from '@veeam-vspc/components';

import { AdvancedGrid } from 'views/components/AdvancedGrid';
import { core } from 'core/core-module';
import { useLang } from 'views/providers/LangProvider/hooks';
import { PulseLicenseInstallationStatus } from '../../../../enums';
import { Toolbar } from './components/Toolbar';
import { useAppServices, useAppStore } from 'views/providers/AppProvider/hooks';
import { normalizeDateWithTime } from 'core/utils/date-helpers';

import type { VbrDeploySettingsWizardModel } from '../../../../entries';
import type {
    VbrLicenseRequestParams,
    VbrLicenseKey,
    PulseLicenseKey,
    PulseLicenseRequestParams,
} from '../../../../interfaces';

export const LicenseGrid: React.FC<WizardStepData<VbrDeploySettingsWizardModel>> = ({ data, onDataChange, stepData }) => {
    const lang = useLang();
    const { formats } = useAppStore();
    const { notificationService, enumMapperService } = useAppServices();

    return (
        <AdvancedGrid
            stateId={'VbrWizard.PulseLicense.LicenseGrid'}
            columns={[
                { field: 'productName', title: lang.PRODUCT },
                { field: 'points', title: lang.POINTS },
                {
                    field: 'expirationDate',
                    title: lang.EXPIRATION_DATE,
                    cell: ({ cellData }) => <TextColumn>{normalizeDateWithTime(new Date(Date.parse(cellData)), formats)}</TextColumn>,
                },
                {
                    field: 'serverName',
                    title: lang.HOSTNAME,
                    cell: ({ rowData }: CellRendererProps<PulseLicenseKey>) =>
                        <TextColumn>{rowData.serverName || lang.DASH_SYMBOL}</TextColumn>,
                    hidden: true,
                },
                {
                    field: 'licenseUsageType',
                    title: lang.LICENSE_TYPE,
                    cell: ({ rowData }: CellRendererProps<PulseLicenseKey>) =>
                        <TextColumn>{enumMapperService.getEnumDescription('PulseLicenseUsageType', rowData.licenseUsageType)}</TextColumn>,
                    hidden: true,
                },
                {
                    field: 'licenseId',
                    title: lang.LICENSE_ID,
                    hidden: true,
                },
            ]}
            initialState={{
                filters: {
                    productUids: data.productUids,
                    installationStatus: [PulseLicenseInstallationStatus.Installed],
                },
                selected: data.licenseId ? [{ licenseUid: data.licenseId } as VbrLicenseKey] : undefined,
            }}
            data={(requestParams: VbrLicenseRequestParams) => core.transportService
                .request<PulseLicenseRequestParams, PulseLicenseKey[]>('Pulse/GetOrganizationLicenseKeys', {
                    productUids: requestParams.filter.productUids || [],
                    organizationUid: data.organizationUid,
                })}
            selection={{
                field: 'licenseUid',
                multiple: false,
                checkbox: false,
            }}
            onSelectionChange={(selected) => {
                const selectedLicense = selected[0];

                data.licenseId = selectedLicense?.licenseUid;

                if (selectedLicense.installationStatus) {
                    if (selectedLicense.installationStatus === PulseLicenseInstallationStatus.Installed) {
                        let isLicenseAlreadyInstalledPromise = null;

                        stepData.isLicenseAlreadyInstalled = () => {
                            if (!isLicenseAlreadyInstalledPromise) {
                                isLicenseAlreadyInstalledPromise = new Promise(resolve =>
                                    notificationService.info(
                                        capitalize(lang.INSTALL_LICENSE),
                                        lang.IT_IS_NOT_RECOMMENDED_TO_SHARE,
                                        {
                                            buttons: [
                                                {
                                                    type: NotificationDialogsTextKeys.Ok,
                                                    text: lang.COPY,
                                                },
                                                {
                                                    type: NotificationDialogsTextKeys.Cancel,
                                                    text: lang.CANCEL,
                                                    view: ACTION_VIEW.secondary,
                                                },
                                            ],
                                        }
                                    ).then((btn) => {
                                        if (btn !== NotificationDialogsTextKeys.Ok) {
                                            isLicenseAlreadyInstalledPromise = null;
                                        }
                                        resolve(btn === NotificationDialogsTextKeys.Ok);
                                    })
                                );
                            }

                            return isLicenseAlreadyInstalledPromise;
                        };
                    } else {
                        delete stepData.isLicenseAlreadyInstalledPromise;
                        delete stepData.isLicenseAlreadyInstalled;
                    }
                }

                onDataChange(data);
            }}
            disablePagination
            toolbars={[
                () => <Toolbar data={data} onDataChange={onDataChange} />,
            ]}
        />
    );
};

